/// ////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
/// 🔐 usePermissions — ergonomic accessor for the permissions store.
///
/// Most components that need to gate behavior just want a `can(...)`
/// function and a couple of booleans. Importing the store and pulling
/// refs through `storeToRefs` every time gets noisy, so this composable
/// returns the surface most components actually use.
///
/// Example:
///   const { can, isAdmin, isSuperAdmin, activeRoleName } = usePermissions()
///   if (cannot('users:invite')) return
///
/// The store is reactive, so anything you destructure here that's a
/// `computed` or `ref` stays reactive. Functions (`can`, `cannot`)
/// always read live state.
/// ////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////

import { storeToRefs } from 'pinia'
import { usePermissionsStore } from '~/stores/permissionsStore'

export const usePermissions = () => {
  const store = usePermissionsStore()
  const {
    granted,
    activeRoleId,
    activeRoleName,
    isAdmin,
    isSuperAdmin,
    loaded,
    loading,
    rolesById,
  } = storeToRefs(store)

  /**
   * Build the props for a "show-but-disable-if-no-permission" button.
   *
   * Returns `{ disabled, title }` ready to spread into a `<button>`
   * via `v-bind="gate(...)"`. When the user lacks the permission, the
   * button is disabled (so Tailwind's `disabled:opacity-50 disabled:cursor-not-allowed`
   * variants paint the greyed-out state) and a hover tooltip explains
   * why. When the user has the permission, both fields are empty so
   * the button behaves normally.
   *
   * Example:
   *   <button v-bind="gate('users:invite', 'create users')" @click="...">
   *     Create User
   *   </button>
   *
   * The `actionLabel` is the verb phrase that follows "permission to"
   * — e.g. "create users", "modify roles", "drop the role".
   */
  function gate(permission: string | string[], actionLabel: string): { disabled: boolean, title: string } {
    if (store.can(permission)) return { disabled: false, title: '' }
    return {
      disabled: true,
      title: `You don't have permission to ${actionLabel}. Ask an admin for the required role permission.`,
    }
  }

  /**
   * Combined gate that checks BOTH the Pulsar role permission AND a
   * Snowflake-side capability. Used on buttons that mutate Snowflake
   * objects — both checks must pass for the button to be active.
   *
   * Pulsar permission is checked first because that's the more common
   * blocker and usually requires admin intervention to fix. The
   * Snowflake check is reported only when the Pulsar check passes —
   * otherwise the Pulsar tooltip explains everything the user needs
   * to know.
   *
   * Pass `snowflake` as the `SnowflakeCapability` returned by the
   * `useSnowflakeAccountPrivileges` composable. In a Vue template you
   * can pass the computed directly — Vue auto-unwraps refs in
   * v-bind/expressions.
   *
   * Example:
   *   const sf = useSnowflakeAccountPrivileges()
   *   <button v-bind="gateSnowflake('users:invite', 'create users', sf.canManageUsers)" ...>
   */
  function gateSnowflake(
    permission: string | string[],
    actionLabel: string,
    snowflake:
      | { ok: boolean, reason: string, activeRole?: string, switchableTo?: string | null, requiredRoles?: string[] }
      | { value: { ok: boolean, reason: string, activeRole?: string, switchableTo?: string | null, requiredRoles?: string[] } },
  ): { disabled: boolean, title: string } {
    // Pulsar check first — it's the more common blocker and the fix
    // (ask an admin) is more useful to surface than the Snowflake-side
    // suggestion.
    const pulsar = gate(permission, actionLabel)
    if (pulsar.disabled) return pulsar

    // Auto-unwrap if the caller passed a Ref<SnowflakeCapability>
    // (e.g. directly from script-setup land where computed values
    // aren't unwrapped automatically).
    const sf = ('value' in snowflake && snowflake.value && typeof snowflake.value === 'object')
      ? snowflake.value
      : (snowflake as { ok: boolean, reason: string, activeRole?: string, switchableTo?: string | null, requiredRoles?: string[] })

    if (sf.ok) return { disabled: false, title: '' }

    // Compose action-specific copy from the structured SnowflakeCapability
    // fields ("Your role SYSADMIN doesn't have permission to create users.
    // Switch to USERADMIN…") so PermissionTooltip can show a clear,
    // actor-relevant message. We fall back to the legacy `reason` string
    // for any caller still passing the old `{ ok, reason }` plain object.
    const role = sf.activeRole || 'unknown'
    const requiredRoles = sf.requiredRoles ?? []
    const switchable = sf.switchableTo ?? null

    if (!requiredRoles.length) return { disabled: true, title: sf.reason }

    const fix = switchable
      ? `Switch to ${switchable} via the role switcher in the top bar — your account also has access to ${requiredRoles.join(', ')} for these operations.`
      : `Ask a Snowflake admin to grant you one of: ${requiredRoles.join(', ')}.`

    return {
      disabled: true,
      title: `Your role ${role} doesn't have permission to ${actionLabel}. ${fix}`,
    }
  }

  return {
    // reactive refs
    granted,
    activeRoleId,
    activeRoleName,
    isAdmin,
    isSuperAdmin,
    loaded,
    loading,
    rolesById,
    // function checks (always read live state via the store)
    can: store.can,
    cannot: store.cannot,
    gate,
    gateSnowflake,
    sanitizeForAccountRole: store.sanitizeForAccountRole,
    // lifecycle (rarely needed in components — used by the auth plugin)
    bind: store.bind,
    teardown: store.teardown,
  }
}
